import React, { useState } from 'react';
import { Star, Send, MessageSquare, Lock } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    text: '',
    authCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStarClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.text) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    if (formData.text.length < 10) {
      toast.error('Ihre Bewertung sollte mindestens 10 Zeichen lang sein');
      return;
    }

    if (!formData.authCode) {
      toast.error('Bitte geben Sie den Authentifizierungscode ein');
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData = {
        name: formData.name,
        rating: formData.rating,
        text: formData.text,
        date: new Date().toISOString().split('T')[0],
        authCode: formData.authCode
      };

      await axios.post(`${API}/testimonials`, reviewData);

      toast.success('Vielen Dank für Ihre Bewertung!', {
        description: 'Ihre Bewertung wurde erfolgreich veröffentlicht.'
      });

      // Reset form
      setFormData({
        name: '',
        rating: 5,
        text: '',
        authCode: ''
      });
    } catch (error) {
      console.error('Fehler beim Absenden:', error);
      if (error.response?.status === 403) {
        toast.error('Ungültiger Authentifizierungscode', {
          description: 'Der eingegebene Code ist nicht korrekt.'
        });
      } else {
        toast.error('Fehler beim Absenden der Bewertung', {
          description: 'Bitte versuchen Sie es später erneut.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="review" className="py-20 bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-orange-600 to-blue-600 bg-clip-text text-transparent">
              Bewerten Sie unseren Service
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Ihre Meinung ist uns wichtig! Teilen Sie Ihre Erfahrungen mit anderen Tierbesitzern.
          </p>
        </div>

        <Card className="shadow-xl border-2 border-gray-200">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl">Ihre Bewertung</CardTitle>
                <CardDescription className="text-orange-50">
                  Teilen Sie Ihre Erfahrung mit Dom's Pfotenexpress
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="reviewName" className="text-base font-semibold">
                  Ihr Name *
                </Label>
                <Input
                  id="reviewName"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="z.B. Maria S."
                  className="h-12 text-lg"
                />
                <p className="text-xs text-gray-500">
                  Tipp: Sie können auch nur Ihren Vornamen und Anfangsbuchstaben verwenden
                </p>
              </div>

              {/* Star Rating */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Bewertung *</Label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleStarClick(star)}
                      className="transition-transform hover:scale-110 focus:outline-none"
                    >
                      <Star
                        className={`h-10 w-10 ${
                          star <= formData.rating
                            ? 'fill-orange-500 text-orange-500'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-4 text-lg font-semibold text-gray-700">
                    {formData.rating} {formData.rating === 1 ? 'Stern' : 'Sterne'}
                  </span>
                </div>
              </div>

              {/* Review Text */}
              <div className="space-y-2">
                <Label htmlFor="reviewText" className="text-base font-semibold">
                  Ihre Erfahrung *
                </Label>
                <Textarea
                  id="reviewText"
                  required
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  placeholder="Beschreiben Sie Ihre Erfahrung mit unserem Service. Was hat Ihnen besonders gefallen? Was können wir besser machen? Ihr Lob und Ihre Kritik sind uns Gold wert."
                  rows={6}
                  className="text-base"
                />
                <p className="text-xs text-gray-500">
                  Mindestens 10 Zeichen · {formData.text.length} Zeichen
                </p>
              </div>

              {/* Authentication Code */}
              <div className="space-y-2">
                <Label htmlFor="authCode" className="text-base font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4 text-orange-600" />
                  Authentifizierungscode *
                </Label>
                <Input
                  id="authCode"
                  required
                  type="text"
                  value={formData.authCode}
                  onChange={(e) => handleInputChange('authCode', e.target.value)}
                  placeholder="Code eingeben"
                  className="h-12 text-lg"
                  data-testid="review-auth-code-input"
                />
                <p className="text-xs text-gray-500">
                  Geben Sie den Code ein, den Sie von uns erhalten haben
                </p>
              </div>

              {/* Info Notice */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Um die Qualität und Authentizität der Bewertungen zu gewährleisten, 
                  benötigen Sie einen Authentifizierungscode zur Veröffentlichung. Diesen Code erhalten Sie von uns nach Inanspruchnahme unserer Dienstleistung.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 transition-all duration-300"
              >
                <Send className="h-5 w-5 mr-2" />
                {isSubmitting ? 'Wird gesendet...' : 'Bewertung absenden'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Vielen Dank, dass Sie sich die Zeit nehmen, uns zu bewerten! 
            Ihr Feedback hilft uns, unseren Service kontinuierlich zu verbessern.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReviewForm;
